<template>
  <div class="w-full">
    <div v-if="hasLabel" class="mb-1 flex justify-between">
      <label :for="label" class="text-sm font-bold">
        {{ label }}
      </label>
      <span v-if="optional" class="text-xs text-text-dark">({{ optional }})</span>
    </div>

    <div class="relative w-full">
      <PVSelect
        v-bind="$attrs"
        :model-value="modelValue"
        :placeholder="placeholder"
        :options="options"
        :option-label="optionLabel"
        :aria-labelledby="hasLabel ? label : undefined"
        :aria-expanded="isOpen"
        :aria-activedescendant="activeOption"
        :aria-invalid="error ? 'true' : 'false'"
        :invalid="error ? true : false"
        :disabled="disabled"
        :pt="{
          root: ({ state, props }: any) => ({
            class: ['input-wrapper'],
          }),
          label: ({ }: any) => ({
            class: [
              'input-field',
              'flex',
              'text-sm',
              'font-quicksand',
              'text-text-dark',
              'font-normal',
              'shadow-xs',
              'w-full',
              'py-3',
              'px-6',
              'border',
              'border-border-light',
              'bg-background-03',
              'outline-none',
              'rounded-md',
              'rounded-tl-none',
              'focus:outline-none',
              'focus:ring-2',
              'focus:ring-accent-03',
              'focus:ring-offset-2',
              'disabled:opacity-50',
              'transition-all',
              'duration-200',
              error ? 'border-states-error focus:ring-states-error focus:border-border-light' : '',
            ],
          }),
          dropdown: {
            class: ['absolute', 'top-1/2', 'right-3', 'translate-y-[-50%]'],
          },
          overlay: {
            class: [
              'bg-background-03',
              'absolute top-0 left-0',
              'border',
              'border-gray-light-300',
              'rounded-md',
              'rounded-tl-none',
              'shadow-lg',
            ],
          },
          listContainer: {
            class: ['max-h-[200px]', 'overflow-auto','overscroll-contain'],
          },
          list: {
            class: 'p-0 list-none m-0',
          },
          option: ({ context, state }: any) => ({
            class: [
              'p-0',
              'rounded-md',
              'rounded-tl-none',
              'text-sm',
              'cursor-pointer',
              'select-none',
              'transition-all',
              'duration-200',
              'text-text-dark',
              'hover:bg-background-01',
              'focus:bg-background-01',
              'focus:outline-none',
              'focus:ring-2',
              'focus:ring-accent-03',
              context.focused ? 'bg-background-01' : '',
            ],
          }),
          transition: {
            enterFromClass: 'opacity-0 scale-y-[0.8] text-accent-03',
            enterActiveClass:
              'transition-[transform,opacity] duration-[120ms] ease-[cubic-bezier(0,0,0.2,1)]',
            leaveActiveClass: 'transition-opacity duration-100 ease-linear',
            leaveToClass: 'opacity-0',
          },
        }"
        @update:model-value="handleOptionSelect"
        @focus="() => {isOpen = true;}"
        @show="() => scrollDisabled = true"
        @blur="() => {isOpen = false ; scrollDisabled = false;}"
      >
        <template #dropdownicon>
          <BaseIcon class="size-5" icon="ChevronDown"></BaseIcon>
        </template>
        <template #value="slotProps">
          <div class="flex items-center gap-2 overflow-hidden">
            <span class="mr-4 truncate">{{
              slotProps.value ? slotProps.value.label : slotProps.placeholder
            }}</span>
          </div>
        </template>
        <template #option="slotProps">
           <div
              class="option-content flex items-center justify-between gap-2 px-6 py-3"
              :class="{
                'bg-background-01 font-medium': slotProps.selected,
              }"
            >
            <div class="flex items-center gap-2">
              
              <p v-if="!navigateOnClick" >
                {{ slotProps.option.label }}
              </p>

              <a v-else :href="slotProps.option.value" target="_self" class="unset no-underline text-text-dark">
                {{ slotProps.option.label }}
              </a>

            </div>
            <BaseIcon v-if="slotProps.selected" class="size-5" icon="Check" />
          </div>
        </template>
      </PVSelect>
    </div>

    <div v-if="error || hint" class="mt-1 text-xs">
      <p v-if="error" class="input-error">{{ error }}</p>
      <p v-else>{{ hint }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseIcon from '../BaseIcon/BaseIcon.vue'
import { ref, watch, onUnmounted } from 'vue'
import type {
  IBaseInputDropdown,
  IBaseInputDropdownOptions,
} from '@/common/components/base/BaseInputDropdown/IBaseInputDropdown'
import { Select as PVSelect } from 'primevue'

const isOpen = ref(false);
const scrollDisabled = ref(false);
const activeOption = ref<string | null>(null);

const props = withDefaults(defineProps<IBaseInputDropdown>(), {
  placeholder: 'placeholder',
  hasLabel: false,
  optionLabel: 'label',
  error: '',
  optional: '',
  disabled: false,
  navigateOnClick: false,
  modelValue: null,
})

const emits = defineEmits<{
  (e: 'update:modelValue', v: IBaseInputDropdownOptions | null): void
}>()

const handleOptionSelect = (option: IBaseInputDropdownOptions | null) => {
  isOpen.value = false;
  emits('update:modelValue', option); 
};

// Prevent body scroll when dropdown is open
watch(scrollDisabled, (newValue) => {
  if (scrollDisabled.value) {
    // Disable page scrolling
    document.body.style.overflow = 'hidden';
  } else {
    // Re-enable page scrolling
    document.body.style.overflow = '';
  }
});

onUnmounted(() => {
  document.body.style.overflow = '';
});

</script>

<style scoped>
.input-wrapper {
  @apply relative w-full cursor-pointer select-none rounded-md rounded-tl-none outline-none transition-all duration-200;
}

.input-error {
  @apply text-states-error;
}

:deep(.option-content.p-highlight) {
  @apply bg-background-01;
}

:deep(.p-focus) {
  @apply bg-background-01 ring-2 ring-accent-03 outline-none;
}

.input-wrapper {
  @apply relative w-full cursor-pointer select-none rounded-md rounded-tl-none outline-none transition-all duration-200;
}

.input-error {
  @apply text-states-error;
}

</style>
